import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { useShiftStore } from '@/stores/shift';
import { useActivePatients, useOrientedPatients } from '@/stores/patients';
import { PatientListTile } from '@/components/PatientListTile';
import { useConfirm } from '@/components/ConfirmDialog';
import { discardCurrentShift } from '@/services/firestore/shifts';
import { ReceiveTransferButton } from '@/features/transfer/ReceiveTransferButton';
import { TransferDialog } from '@/features/transfer/TransferDialog';

type Tab = 'active' | 'oriented';

export function ShiftTabsPage() {
  const profile = useAuthStore((s) => s.profile);
  const verifyShift = useShiftStore((s) => s.verifyCurrentShift);
  const currentShift = useShiftStore((s) => s.currentShift);
  const cachedShiftTotal = useShiftStore((s) => s.cachedShiftTotal);
  const cachedHoursBilled = useShiftStore((s) => s.cachedHoursBilled);
  const resetShift = useShiftStore((s) => s.resetShift);

  const active = useActivePatients();
  const oriented = useOrientedPatients();
  const confirm = useConfirm();

  const [tab, setTab] = useState<Tab>('active');
  const [busy, setBusy] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferHelpOpen, setTransferHelpOpen] = useState(false);

  useEffect(() => {
    if (!profile) return;
    void verifyShift(profile.uid);
    void active.refresh(profile.uid);
    void oriented.refresh(profile.uid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.uid]);

  if (!profile) return null;

  const list = tab === 'active' ? active.list : oriented.list;
  const loading = tab === 'active' ? active.isLoading : oriented.isLoading;
  const hasShift = !!currentShift.dateStarted;

  async function onCancelShift() {
    if (!profile) return;
    const totalPatients = active.list.length + oriented.list.length;
    const ok = await confirm({
      title: 'Annuler le quart',
      message:
        totalPatients > 0
          ? `Supprimer le quart en cours, ${totalPatients} patient${totalPatients > 1 ? 's' : ''} et tous les actes ? Cette action est irréversible.`
          : 'Supprimer le quart en cours ? Cette action est irréversible.',
      confirmLabel: 'Annuler le quart',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await discardCurrentShift(profile.uid);
      resetShift();
      await Promise.all([
        active.refresh(profile.uid),
        oriented.refresh(profile.uid),
      ]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="p-4 flex flex-col gap-3">
      <header className="flex items-center justify-between px-2">
        <h1 className="text-2xl font-semibold">Mon quart</h1>
        <div className="text-right">
          <p className="text-[11px] text-white/60 leading-none">Total facturé</p>
          <p className="text-lg font-semibold leading-tight mt-1">
            {cachedShiftTotal + cachedHoursBilled} $
          </p>
          {cachedHoursBilled > 0 && (
            <p className="text-[10px] text-white/50 leading-none mt-0.5">
              dont {cachedHoursBilled} $ forfait
            </p>
          )}
        </div>
      </header>

      {!hasShift && (
        <div className="bg-brand-dark/40 rounded-2xl p-4 text-sm text-white/70">
          Aucun quart en cours.{' '}
          <Link to="/new-patient" className="underline">
            Ajouter un patient
          </Link>{' '}
          pour démarrer.
        </div>
      )}

      <div className="flex bg-brand-dark/30 rounded-pill p-1">
        <button
          type="button"
          onClick={() => setTab('active')}
          className={`flex-1 py-2 rounded-pill text-sm font-semibold transition-colors ${
            tab === 'active'
              ? 'bg-brand-orange text-white'
              : 'text-white/70'
          }`}
        >
          Actifs ({active.list.length})
        </button>
        <button
          type="button"
          onClick={() => setTab('oriented')}
          className={`flex-1 py-2 rounded-pill text-sm font-semibold transition-colors ${
            tab === 'oriented'
              ? 'bg-brand-green text-white'
              : 'text-white/70'
          }`}
        >
          Réglés ({oriented.list.length})
        </button>
      </div>

      <section className="flex flex-col gap-2">
        {loading && <p className="text-sm text-white/60 px-2">Chargement…</p>}
        {!loading && list.length === 0 && (
          <p className="text-sm text-white/60 px-2">
            Aucun patient {tab === 'active' ? 'actif' : 'réglé'}.
          </p>
        )}
        {list.map((p) => (
          <PatientListTile key={p.id} patient={p} />
        ))}
      </section>

      {hasShift && (
        <div className="flex flex-col gap-2 mt-2 px-2">
          <div className="flex gap-2">
            <Link
              to="/new-patient"
              className="flex-1 rounded-pill bg-brand-orange text-white font-semibold py-2 text-center"
            >
              + Patient
            </Link>
            <Link
              to="/shift/end"
              className="flex-1 rounded-pill bg-white text-brand-darkest font-semibold py-2 text-center"
            >
              Soumettre
            </Link>
          </div>
          <button
            type="button"
            onClick={() => void onCancelShift()}
            disabled={busy}
            className="text-xs text-red-300 underline self-center mt-1 disabled:opacity-50"
          >
            {busy ? 'Suppression…' : 'Annuler le quart'}
          </button>
        </div>
      )}

      {hasShift && (
        <section className="mt-4 px-2 flex flex-col gap-3">
          <h2 className="text-xs uppercase tracking-wide text-white/55 px-1">
            Transfert de patients
          </h2>
          <div className="flex items-stretch gap-2">
            <button
              type="button"
              disabled={active.list.length === 0}
              onClick={() => setTransferDialogOpen(true)}
              className="flex-1 rounded-pill bg-white/10 text-white text-sm font-semibold px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Transfert à un(e) collègue
              {active.list.length === 0 && (
                <span className="block text-[10px] font-normal text-white/50 mt-0.5">
                  Aucun patient actif
                </span>
              )}
            </button>
            <button
              type="button"
              aria-label="À propos du transfert"
              onClick={() => setTransferHelpOpen((v) => !v)}
              className="shrink-0 w-9 rounded-full bg-white/10 text-white/80 text-sm font-semibold"
            >
              ⓘ
            </button>
          </div>
          {transferHelpOpen && (
            <p className="text-xs text-white/70 bg-white/5 rounded-xl px-3 py-2">
              Seuls les patients <strong>actifs</strong> peuvent être
              transférés. Les patients déjà <strong>réglés</strong> ne sont pas
              transférables.
            </p>
          )}
          <ReceiveTransferButton />
        </section>
      )}

      <TransferDialog
        open={transferDialogOpen}
        uid={profile.uid}
        onClose={() => setTransferDialogOpen(false)}
      />
    </div>
  );
}
